/**
 * Drop-off locations (TASK-0256, DROP-1): a vendor can deliver to the store directly or to a place that later
 * brings the goods to the store (a consolidation point / cross-dock / ship-to; "Drop-off location" on screen).
 * Pure and dependency-free, so the API (timing, the Send snapshot, the order text) and the console (the board,
 * the Send dialog, How we order) compute the same days and the same words.
 *
 * Days are store calendar days as `YYYY-MM-DD`, read as UTC so no zone ever moves them. Weekdays are 0-6
 * (0 = Sunday), as every purchasing route sends them.
 *
 * The rules:
 * - The store is the default destination and is never a location ("Direct to the store" = null).
 * - `transferDays` is the fewest days from the drop to the store; `transferWeekdays` the weekdays goods reach
 *   the store from there (null = any day). `storeArrivalOn` turns a drop day into the store day.
 * - The drop-by time is wording only: we do not know when the vendor drops, so it never moves a date.
 * - The vendor is told ITS day (the drop day); every timing rule in the store uses the store day.
 * - No location is named in code: staff enter them.
 */

/* ── days ──────────────────────────────────────────────────────────────── */

const DAY_MS = 86_400_000;
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function dayNumber(isoDay: string): number {
  return Math.round(Date.parse(`${isoDay}T00:00:00Z`) / DAY_MS);
}

export function addStoreDays(isoDay: string, days: number): string {
  return new Date((dayNumber(isoDay) + days) * DAY_MS).toISOString().slice(0, 10);
}

export function storeDaysBetween(fromDay: string, toDay: string): number {
  return dayNumber(toDay) - dayNumber(fromDay);
}

export function storeWeekdayOf(isoDay: string): number {
  return new Date(dayNumber(isoDay) * DAY_MS).getUTCDay();
}

export function isStoreDay(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const t = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === value;
}

/** `Mon`, `Tue Thu`, or null for none. */
export function weekdaysText(days: readonly number[] | null | undefined): string | null {
  if (!days || days.length === 0) return null;
  return [...new Set(days)]
    .sort((a, b) => a - b)
    .map((d) => WEEKDAY_NAMES[((d % 7) + 7) % 7])
    .join(" ");
}

/* ── the transfer from a location to the store ─────────────────────────── */

/** How goods dropped at a location reach the store. */
export interface DropOffSchedule {
  /** The fewest days from the drop to the store, 0-14. */
  transferDays: number;
  /** The weekdays goods reach the store from there (0-6); null or empty = any day. */
  transferWeekdays: readonly number[] | null;
}

export const TRANSFER_DAYS_MAX = 14;
/** How far `storeArrivalOn` looks for a transfer weekday. */
export const STORE_ARRIVAL_SEARCH_DAYS = 14;

/**
 * The day goods dropped on `dropOn` reach the store: the first day on or after `dropOn + transferDays`
 * whose weekday is a transfer weekday (any day when none is set). Looks at most 14 days on, else
 * `dropOn + transferDays`. A transfer of 1 day on Tuesdays: a Monday drop reaches the store Tuesday, a
 * Tuesday drop the next Tuesday.
 */
export function storeArrivalOn(dropOn: string, schedule: DropOffSchedule): string {
  const transfer = Math.max(0, Math.min(TRANSFER_DAYS_MAX, Math.round(schedule.transferDays)));
  const earliest = addStoreDays(dropOn, transfer);
  const days = schedule.transferWeekdays;
  if (!days || days.length === 0) return earliest;
  for (let d = 0; d <= STORE_ARRIVAL_SEARCH_DAYS; d++) {
    const day = addStoreDays(earliest, d);
    if (days.includes(storeWeekdayOf(day))) return day;
  }
  return earliest;
}

/* ── the snapshot on a PO ──────────────────────────────────────────────── */

/**
 * `purchase_orders.ship_to`: the location as it was when the order was placed (or re-planned). Editing the
 * location later changes new orders only; an open PO keeps this until staff change its delivery.
 */
export interface ShipToSnapshotV1 {
  v: 1;
  locationId: number;
  name: string;
  address: string | null;
  /** The location's own wording for the order text, null = the generated line. */
  deliverToLine: string | null;
  transferDays: number;
  /** 0-6; null = any day. */
  transferWeekdays: number[] | null;
  /** `HH:MM`, wording only. */
  dropCutoff: string | null;
}

/** A location as the snapshot and the order text need it. */
export interface DropOffLocationFacts {
  id: number;
  name: string;
  address: string | null;
  deliverToLine: string | null;
  transferDays: number;
  transferWeekdays: number[] | null;
  dropCutoffLocal: string | null;
}

export function shipToSnapshotOf(location: DropOffLocationFacts): ShipToSnapshotV1 {
  return {
    v: 1,
    locationId: location.id,
    name: location.name,
    address: location.address,
    deliverToLine: location.deliverToLine,
    transferDays: location.transferDays,
    transferWeekdays:
      location.transferWeekdays && location.transferWeekdays.length > 0
        ? [...location.transferWeekdays].sort((a, b) => a - b)
        : null,
    dropCutoff: location.dropCutoffLocal,
  };
}

/** A stored snapshot, or null when the PO is direct (or the json is not one we wrote). */
export function readShipToSnapshot(value: unknown): ShipToSnapshotV1 | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (r.v !== 1 || typeof r.locationId !== "number" || typeof r.name !== "string") return null;
  const str = (x: unknown) => (typeof x === "string" && x !== "" ? x : null);
  const weekdays = Array.isArray(r.transferWeekdays)
    ? r.transferWeekdays.filter((d): d is number => typeof d === "number" && Number.isInteger(d) && d >= 0 && d <= 6)
    : null;
  return {
    v: 1,
    locationId: r.locationId,
    name: r.name,
    address: str(r.address),
    deliverToLine: str(r.deliverToLine),
    transferDays: typeof r.transferDays === "number" ? r.transferDays : 0,
    transferWeekdays: weekdays && weekdays.length > 0 ? weekdays : null,
    dropCutoff: str(r.dropCutoff),
  };
}

/** Two snapshots that tell the vendor the same thing (the id, the words and the timing). */
export function sameShipTo(a: ShipToSnapshotV1 | null, b: ShipToSnapshotV1 | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.locationId === b.locationId &&
    a.name === b.name &&
    a.address === b.address &&
    a.deliverToLine === b.deliverToLine &&
    a.transferDays === b.transferDays &&
    JSON.stringify(a.transferWeekdays) === JSON.stringify(b.transferWeekdays) &&
    a.dropCutoff === b.dropCutoff
  );
}

/**
 * The day the VENDOR is told: the drop day when the order goes to a location, else the expected (store)
 * day. With a location and no drop day the order names no day ("Order").
 */
export function vendorDeliveryDay(input: {
  shipTo: ShipToSnapshotV1 | null | undefined;
  dropOn: string | null | undefined;
  expectedOn: string | null | undefined;
}): string | null {
  if (input.shipTo) return input.dropOn ?? null;
  return input.expectedOn ?? null;
}

/* ── the order text's "Please deliver to" line ─────────────────────────── */

/** What the order text needs to say where to deliver (VendorOrderContext.deliverTo). */
export interface DeliverTo {
  name: string;
  address: string | null;
  /** The location's own wording (placeholders below), null = the generated line. */
  line: string | null;
  /** `HH:MM`, wording only. */
  cutoff: string | null;
}

export function deliverToOf(snapshot: ShipToSnapshotV1 | null | undefined): DeliverTo | null {
  if (!snapshot) return null;
  return { name: snapshot.name, address: snapshot.address, line: snapshot.deliverToLine, cutoff: snapshot.dropCutoff };
}

/** The placeholders a location's "Wording on orders" may use. */
export const DELIVER_TO_PLACEHOLDERS = ["location", "address", "store", "dropDay", "cutoff"] as const;
export type DeliverToPlaceholder = (typeof DELIVER_TO_PLACEHOLDERS)[number];

/** `2 pm`, `2:30 pm`, `12 pm`, from `HH:MM`; empty when not a time. */
export function cutoffText(cutoff: string | null | undefined): string {
  const m = cutoff ? /^(\d{2}):(\d{2})/.exec(cutoff) : null;
  if (!m) return "";
  const h = Number(m[1]);
  const minutes = m[2]!;
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return minutes === "00" ? `${h12} ${suffix}` : `${h12}:${minutes} ${suffix}`;
}

/** `North Depot, 12 Example Rd` (the address only when set). */
export function deliverToPlace(to: Pick<DeliverTo, "name" | "address">): string {
  const address = to.address?.trim();
  return address ? `${to.name}, ${address}` : to.name;
}

function dropDayText(isoDay: string | null): string {
  if (!isStoreDay(isoDay)) return "";
  const t = new Date(`${isoDay}T00:00:00Z`);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${WEEKDAY_NAMES[t.getUTCDay()]} ${months[t.getUTCMonth()]} ${t.getUTCDate()}`;
}

/**
 * The one line the order text adds after "Order for delivery ...": the location's own wording filled in, else
 * `Please deliver to North Depot, 12 Example Rd, for Kingston Kosher, by 2 pm`. One line, no trailing space.
 */
export function deliverToLineText(to: DeliverTo, ctx: { storeName: string | null; dropOn: string | null }): string {
  const store = ctx.storeName?.trim() ?? "";
  const custom = to.line?.trim();
  if (custom) {
    const values: Record<DeliverToPlaceholder, string> = {
      location: to.name,
      address: to.address?.trim() ?? "",
      store,
      dropDay: dropDayText(ctx.dropOn),
      cutoff: cutoffText(to.cutoff),
    };
    return custom
      .replace(/\{([^{}]*)\}/g, (_, name: string) =>
        (DELIVER_TO_PLACEHOLDERS as readonly string[]).includes(name) ? values[name as DeliverToPlaceholder] : "",
      )
      .replace(/\s+/g, " ")
      .trim();
  }
  const by = cutoffText(to.cutoff);
  return `Please deliver to ${deliverToPlace(to)}${store ? `, for ${store}` : ""}${by ? `, by ${by}` : ""}`;
}

/* ── validation (the API's create / update body, and the console's form) ── */

export const DROP_OFF_LIMITS = {
  name: 80,
  address: 300,
  contactName: 128,
  phone: 64,
  notes: 2000,
  deliverToLine: 300,
} as const;

export interface DropOffLocationInput {
  name: string;
  address: string | null;
  contactName: string | null;
  phone: string | null;
  notes: string | null;
  transferDays: number;
  transferWeekdays: number[] | null;
  dropCutoffLocal: string | null;
  deliverToLine: string | null;
  active: boolean;
  /** TASK-0257 (DROP-2a): how a charge here is shared over order lines (default cases). */
  chargeSplit: "cases" | "value" | "weight";
  /** TASK-0257 (DROP-2a): the vendor whose invoices carry this location's charges (null: not set). */
  billingVendorId: number | null;
}

const LOCATION_KEYS: ReadonlyArray<keyof DropOffLocationInput> = [
  "name",
  "address",
  "contactName",
  "phone",
  "notes",
  "transferDays",
  "transferWeekdays",
  "dropCutoffLocal",
  "deliverToLine",
  "active",
  "chargeSplit",
  "billingVendorId",
];

export type DropOffParse<T> = { success: true; data: T } | { success: false; errors: string[] };

/** The placeholders a wording uses that we do not know, as `{x}`. */
export function unknownDeliverToPlaceholders(text: string): string[] {
  return [...text.matchAll(/\{([^{}]*)\}/g)]
    .map((m) => m[1] ?? "")
    .filter((p) => !(DELIVER_TO_PLACEHOLDERS as readonly string[]).includes(p));
}

/**
 * Validate a location (POST: every required field; `partial`: PUT, only the fields given). Blank text clears
 * to null; every problem is a sentence the operator can act on. The keys `updatedAt` (PUT) are the caller's.
 */
export function parseDropOffLocation(
  input: unknown,
  opts: { partial?: boolean; allowKeys?: readonly string[] } = {},
): DropOffParse<Partial<DropOffLocationInput>> {
  const errors: string[] = [];
  if (input === null || typeof input !== "object" || Array.isArray(input)) {
    return { success: false, errors: ["The location is missing."] };
  }
  const raw = input as Record<string, unknown>;
  const allowed = new Set<string>([...LOCATION_KEYS, ...(opts.allowKeys ?? [])]);
  for (const key of Object.keys(raw)) if (!allowed.has(key)) errors.push(`"${key}" is not a location field.`);
  const out: Partial<DropOffLocationInput> = {};

  const text = (key: keyof typeof DROP_OFF_LIMITS & keyof DropOffLocationInput, label: string, oneLine: boolean) => {
    const v = raw[key];
    if (v === undefined) return;
    if (v === null) {
      (out as Record<string, unknown>)[key] = null;
      return;
    }
    if (typeof v !== "string") {
      errors.push(`${label}: text.`);
      return;
    }
    const t = v.trim();
    if (oneLine && /[\r\n]/.test(t)) errors.push(`${label}: one line.`);
    if (t.length > DROP_OFF_LIMITS[key]) errors.push(`${label}: at most ${DROP_OFF_LIMITS[key]} characters.`);
    (out as Record<string, unknown>)[key] = t === "" ? null : t;
  };

  if (raw.name !== undefined || !opts.partial) {
    const v = raw.name;
    if (typeof v !== "string" || v.trim() === "") errors.push("Name: type the location's name.");
    else {
      const t = v.trim().replace(/\s+/g, " ");
      if (t.length > DROP_OFF_LIMITS.name) errors.push(`Name: at most ${DROP_OFF_LIMITS.name} characters.`);
      out.name = t;
    }
  }
  text("address", "Address", true);
  text("contactName", "Contact", true);
  text("phone", "Phone", true);
  text("notes", "Notes", false);
  text("deliverToLine", "Wording on orders", true);
  if (typeof out.deliverToLine === "string") {
    const unknown = unknownDeliverToPlaceholders(out.deliverToLine);
    if (unknown.length > 0) {
      errors.push(
        `Wording on orders: {${unknown[0]}} is not a placeholder we know. Use ${DELIVER_TO_PLACEHOLDERS.map((p) => `{${p}}`).join(", ")}.`,
      );
    }
  }

  if (raw.transferDays !== undefined || !opts.partial) {
    const v = raw.transferDays === undefined ? 1 : raw.transferDays;
    if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || v > TRANSFER_DAYS_MAX) {
      errors.push(`Days from drop to the store: a whole number from 0 to ${TRANSFER_DAYS_MAX}.`);
    } else out.transferDays = v;
  }
  if (raw.transferWeekdays !== undefined) {
    const v = raw.transferWeekdays;
    if (v === null) out.transferWeekdays = null;
    else if (!Array.isArray(v) || v.some((d) => typeof d !== "number" || !Number.isInteger(d) || d < 0 || d > 6)) {
      errors.push("Reaches the store on: weekdays 0 (Sunday) to 6 (Saturday).");
    } else {
      const days = [...new Set(v as number[])].sort((a, b) => a - b);
      out.transferWeekdays = days.length > 0 ? days : null;
    }
  } else if (!opts.partial) out.transferWeekdays = null;
  if (raw.dropCutoffLocal !== undefined) {
    const v = raw.dropCutoffLocal;
    if (v === null || v === "") out.dropCutoffLocal = null;
    else if (typeof v !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(v.trim())) {
      errors.push("Drop by: a time like 14:00.");
    } else out.dropCutoffLocal = v.trim();
  } else if (!opts.partial) out.dropCutoffLocal = null;
  if (raw.active !== undefined) {
    if (typeof raw.active !== "boolean") errors.push("Active: on or off.");
    else out.active = raw.active;
  } else if (!opts.partial) out.active = true;
  if (raw.chargeSplit !== undefined) {
    const v = raw.chargeSplit;
    if (v !== "cases" && v !== "value" && v !== "weight") errors.push("Split by: cases, value or weight.");
    else out.chargeSplit = v;
  } else if (!opts.partial) out.chargeSplit = "cases";
  if (raw.billingVendorId !== undefined) {
    const v = raw.billingVendorId;
    if (v === null) out.billingVendorId = null;
    else if (typeof v !== "number" || !Number.isInteger(v) || v <= 0 || v > 2_147_483_647) {
      errors.push("Bills come from: choose a vendor, or nobody.");
    } else out.billingVendorId = v;
  } else if (!opts.partial) out.billingVendorId = null;
  if (!opts.partial) {
    for (const key of ["address", "contactName", "phone", "notes", "deliverToLine"] as const) {
      if (out[key] === undefined) out[key] = null;
    }
  }

  if (errors.length > 0) return { success: false, errors: [...new Set(errors)] };
  return { success: true, data: out };
}

/* ── the next order: order day -> drop day -> store day ────────────────── */

/** The parts of How we order the next-order rule reads. Weekdays 0-6. */
export interface OrderingProfile {
  orderWeekdays?: readonly number[] | null;
  /** The days the vendor delivers to its destination (the store when direct, else its location). */
  deliveryWeekdays?: readonly number[] | null;
  orderCutoffLocal?: string | null;
  /** Order to the vendor's delivery at its destination. */
  leadTimeDays?: number | null;
}

/** The vendor's destination, when it is a location. */
export interface ChainDestination extends DropOffSchedule {
  locationId: number;
  name: string;
}

export interface NextOrderChain {
  /** The day the order has to go out. */
  orderOn: string;
  /** The day the vendor drops it at the location; null when direct, or when only the store days are known. */
  dropOn: string | null;
  /** The day it is expected AT THE STORE. */
  deliveryOn: string;
  /** Days from asOf to orderOn: 0 = today. */
  orderInDays: number;
  /** The location it goes through, null = direct to the store. */
  via: { locationId: number; name: string } | null;
}

export const DEFAULT_ORDER_LEAD_DAYS = 2;

/**
 * The next order the vendor's rhythm allows from `asOf` (TASK-0249's rule, extended by DROP-1):
 *
 * - With order days: the next order day (today counts until its cutoff, when `nowMinutes` is given), then
 *   the first delivery day at least `lead` days later. For a vendor with a location that is the drop day,
 *   and the store day is `storeArrivalOn(drop)`.
 * - Without order days: the first delivery day an order placed today can make (`lead` days on), ordered
 *   `lead` days before it.
 * - `storeDays` (the days deliveries usually REACH the store, from receipts) stand in when the profile names
 *   no delivery days: they are store days already, so the drop day is unknown and the transfer time is part
 *   of the wait (`lead + transferDays`).
 * - "No delivery expected until" (`quietUntil`) is compared with the store day.
 *
 * A direct vendor gets exactly TASK-0249's days (`dropOn` null). Null when nothing gives a day.
 */
export function nextOrderChain(
  asOf: string,
  profile: OrderingProfile | null | undefined,
  storeDays: readonly number[],
  opts: {
    nowMinutes?: number | null;
    leadDays?: number;
    quietUntil?: string | null;
    destination?: ChainDestination | null;
  } = {},
): NextOrderChain | null {
  const lead = Math.max(0, Math.round(profile?.leadTimeDays ?? opts.leadDays ?? DEFAULT_ORDER_LEAD_DAYS));
  const quietUntil = opts.quietUntil ?? null;
  const dest = opts.destination ?? null;
  const via = dest ? { locationId: dest.locationId, name: dest.name } : null;
  const transfer = dest ? Math.max(0, Math.round(dest.transferDays)) : 0;
  const profileDays =
    profile?.deliveryWeekdays && profile.deliveryWeekdays.length > 0 ? profile.deliveryWeekdays : null;
  const orderDays = profile?.orderWeekdays && profile.orderWeekdays.length > 0 ? profile.orderWeekdays : null;
  const afterQuiet = (storeDay: string) => !quietUntil || storeDaysBetween(quietUntil, storeDay) > 0;
  // A quiet period pushes the search past it: every candidate of a week after it is still looked at.
  const quietDays = quietUntil ? Math.max(0, storeDaysBetween(asOf, quietUntil)) : 0;

  if (!orderDays) {
    if (profileDays) {
      // Delivery days to the destination: the drop day (the store day when direct). The search starts at the
      // lead and `afterQuiet` filters on the STORE day (review F3: starting past the quiet period minus the
      // fewest transfer days skipped a drop whose wait for a transfer weekday already passes it). The search
      // runs a full week past the later of the lead and the day after the quiet period, so a direct vendor
      // looks at exactly TASK-0249's days (review R3-1: `lead + 7 + quietDays` stopped a day short at lead 0).
      const bound = Math.max(lead, quietDays + 1) + 7 + (dest ? STORE_ARRIVAL_SEARCH_DAYS : 0);
      for (let offset = lead; offset < bound; offset++) {
        const drop = addStoreDays(asOf, offset);
        if (!profileDays.includes(storeWeekdayOf(drop))) continue;
        const store = dest ? storeArrivalOn(drop, dest) : drop;
        if (!afterQuiet(store)) continue;
        return {
          orderOn: addStoreDays(drop, -lead),
          dropOn: dest ? drop : null,
          deliveryOn: store,
          orderInDays: offset - lead,
          via,
        };
      }
      return null;
    }
    if (storeDays.length === 0) return null;
    // Store days from receipts: the wait is the lead plus the transfer.
    const wait = lead + transfer;
    const start = quietUntil ? Math.max(wait, storeDaysBetween(asOf, quietUntil) + 1) : wait;
    for (let offset = start; offset < start + 7; offset++) {
      const store = addStoreDays(asOf, offset);
      if (storeDays.includes(storeWeekdayOf(store))) {
        return {
          orderOn: addStoreDays(store, -wait),
          dropOn: null,
          deliveryOn: store,
          orderInDays: offset - wait,
          via,
        };
      }
    }
    return null;
  }

  const cutoff = profile?.orderCutoffLocal ? /^(\d{2}):(\d{2})/.exec(profile.orderCutoffLocal) : null;
  const cutoffMinutes = cutoff ? Number(cutoff[1]) * 60 + Number(cutoff[2]) : null;
  const todayPassed = opts.nowMinutes != null && cutoffMinutes != null && opts.nowMinutes > cutoffMinutes;
  const start = todayPassed ? 1 : 0;
  for (let offset = start; offset < start + 8 + quietDays; offset++) {
    const orderOn = addStoreDays(asOf, offset);
    if (!orderDays.includes(storeWeekdayOf(orderOn))) continue;
    let dropOn: string | null;
    let deliveryOn: string;
    if (profileDays) {
      // The first delivery day to the destination at least `lead` days after the order; a lead of 0 lets it
      // come the day the order goes out (review F13 of TASK-0249).
      let drop = addStoreDays(orderOn, lead);
      for (let d = 0; d < 7; d++) {
        const day = addStoreDays(orderOn, lead + d);
        if (profileDays.includes(storeWeekdayOf(day))) {
          drop = day;
          break;
        }
      }
      dropOn = dest ? drop : null;
      deliveryOn = dest ? storeArrivalOn(drop, dest) : drop;
    } else if (storeDays.length > 0) {
      const wait = lead + transfer;
      let store = addStoreDays(orderOn, wait);
      for (let d = 0; d < 7; d++) {
        const day = addStoreDays(orderOn, wait + d);
        if (storeDays.includes(storeWeekdayOf(day))) {
          store = day;
          break;
        }
      }
      dropOn = null;
      deliveryOn = store;
    } else {
      const drop = addStoreDays(orderOn, lead);
      dropOn = dest ? drop : null;
      deliveryOn = dest ? storeArrivalOn(drop, dest) : drop;
    }
    if (!afterQuiet(deliveryOn)) continue;
    return { orderOn, dropOn, deliveryOn, orderInDays: offset, via };
  }
  return null;
}
